import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminEmpresas from './pages/admin/AdminEmpresas';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSessoes from './pages/admin/AdminSessoes';
import AdminSettings from './pages/admin/AdminSettings';
import AdminForgotPassword from './pages/admin/AdminForgotPassword';
import CompanyConsentPortal from './pages/company/CompanyConsentPortal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Componente original do QR Code
function QRCodePage() {
  const [cnpj, setCnpj] = useState('');
  const [status, setStatus] = useState('CARREGANDO');
  const [qrCode, setQrCode] = useState(null);
  const [empresaNome, setEmpresaNome] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  
  const cnpjRef = useRef(cnpj);
  const statusRef = useRef(status);
  const qrCodeRef = useRef(qrCode);
  cnpjRef.current = cnpj;
  statusRef.current = status;
  qrCodeRef.current = qrCode;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cnpjParam = params.get('cnpj');
    
    if (cnpjParam) {
      setCnpj(cnpjParam);
    } else {
      setErro('CNPJ não fornecido na URL');
      setStatus('BLOQUEADO');
    }
  }, []);

  const obterQRCode = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/access/qr?cnpj=${cnpjRef.current}&_=${Date.now()}`,
        { cache: 'no-store' }
      );
      const data = await response.json();
      
      if (data.success && data.qr_code) {
        qrCodeRef.current = data.qr_code;
        setQrCode(data.qr_code);
        setStatus('AGUARDANDO_LEITURA');
        setErro('');
        setMensagem('');
        return;
      }

      if (data.status === 'CONECTANDO' || data.status === 'AGUARDANDO_QR') {
        setStatus(data.status);
        setMensagem('Aguardando o WhatsApp gerar um novo QR Code...');
        return;
      }

      if (data.status === 'DESCONECTADO' || data.status === 'ERRO') {
        qrCodeRef.current = null;
        setQrCode(null);
        setStatus(data.status);
        setMensagem('Nao foi possivel gerar o QR Code. Tente iniciar a conexao novamente.');
      }
    } catch (error) {
      console.error('Erro ao obter QR Code:', error);
      setErro('Erro ao buscar QR Code no servidor');
    }
  }, []);

  const iniciarWhatsApp = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/access/init-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cnpj: cnpjRef.current })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setErro('');
        setMensagem('');

        // Somente o estado confirmado pelo backend pode marcar a sessao como conectada.
        if (data.status === 'CONECTADO') {
          setStatus('CONECTADO');
          qrCodeRef.current = null;
          setQrCode(null);
          setMensagem('WhatsApp já conectado!');
        } else {
          const statusInicial = data.status || (data.needs_qr ? 'AGUARDANDO_QR' : 'CONECTANDO');
          setStatus(statusInicial);
          setMensagem(data.message || 'Conexao iniciada. Aguardando WhatsApp...');
          if (data.needs_qr || statusInicial === 'AGUARDANDO_QR') {
            setTimeout(obterQRCode, 2000);
          }
        }
      } else {
        setStatus('ERRO');
        setErro(data.error || 'Erro ao iniciar conexao');
      }
    } catch (error) {
      console.error('Erro ao iniciar WhatsApp:', error);
      setErro('Erro ao iniciar conexão');
      setStatus('ERRO');
    }
  }, [obterQRCode]);

  const validarAcesso = useCallback(async () => {
    if (!cnpjRef.current) return;
    
    try {
      const response = await fetch(`${API_URL}/api/access/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cnpj: cnpjRef.current })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setStatus('BLOQUEADO');
        setErro(data.message || data.error);
        return;
      }
      
      setEmpresaNome(data.empresa.nome_empresa || 'Empresa');
      
      if (data.whatsapp.action === 'JA_CONECTADO') {
        setStatus('CONECTADO');
        setMensagem(data.whatsapp.message);
      } else if (data.whatsapp.action === 'GERAR_QR') {
        iniciarWhatsApp();
      } else if (data.whatsapp.action === 'MOSTRAR_QR') {
        setStatus('AGUARDANDO_QR');
        obterQRCode();
      } else if (data.whatsapp.action === 'RESTAURAR_SESSAO') {
        setStatus('CONECTANDO');
        setMensagem(data.whatsapp.message || 'Restaurando sessao salva do WhatsApp...');
        iniciarWhatsApp();
      }
    } catch (error) {
      console.error('Erro ao validar acesso:', error);
      setErro('Erro de conexão com o servidor');
      setStatus('ERRO');
    }
  }, [iniciarWhatsApp, obterQRCode]);

  const verificarStatus = useCallback(async () => {
    // Não verificar se já está conectado ou bloqueado
    if (!cnpjRef.current || statusRef.current === 'BLOQUEADO' || statusRef.current === 'CONECTADO') return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/access/status?cnpj=${cnpjRef.current}&_=${Date.now()}`,
        { cache: 'no-store' }
      );
      const data = await response.json();
      
      console.log('Status recebido:', data); // Debug
      
      if (!data.success) {
        if (data.error === 'Empresa inativa') {
          setStatus('BLOQUEADO');
          setErro('Acesso bloqueado - empresa desativada');
          qrCodeRef.current = null;
          setQrCode(null);
        }
        return;
      }
      
      // Verificar se está conectado
      if (data.conectado === true || data.whatsapp_status === 'CONECTADO') {
        console.log('WhatsApp CONECTADO! Atualizando UI...'); // Debug
        setStatus('CONECTADO');
        qrCodeRef.current = null;
        setQrCode(null);
        setMensagem('WhatsApp conectado com sucesso!');
        setErro('');
        return;
      }

      if (data.whatsapp_status === 'CONECTANDO') {
        setStatus('CONECTANDO');
        setMensagem('Restaurando conexao com o WhatsApp...');
        return;
      }
      
      // O backend permanece em AGUARDANDO_QR ate o celular ler o codigo.
      // Se o QR ja chegou, manter a tela de leitura em vez de escondê-lo.
      if (data.whatsapp_status === 'AGUARDANDO_QR') {
        if (qrCodeRef.current) {
          setStatus('AGUARDANDO_LEITURA');
          return;
        }

        setStatus('AGUARDANDO_QR');
        obterQRCode();
        return;
      }

      if (data.whatsapp_status === 'DESCONECTADO' || data.whatsapp_status === 'ERRO') {
        setStatus(data.whatsapp_status);
        qrCodeRef.current = null;
        setQrCode(null);
        setMensagem('Conexao nao iniciada. Clique para tentar gerar um novo QR Code.');
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  }, [obterQRCode]);

  useEffect(() => {
    if (cnpj) {
      validarAcesso();
    }
  }, [cnpj, validarAcesso]);

  // Polling mais frequente para detectar conexão
  useEffect(() => {
    if (status === 'AGUARDANDO_LEITURA' || status === 'AGUARDANDO_QR' || status === 'CONECTANDO') {
      // Verificar a cada 2 segundos para resposta mais rápida
      const interval = setInterval(verificarStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [status, verificarStatus]);

  // Atualizar QR Code periodicamente (WhatsApp muda a cada ~20 segundos)
  useEffect(() => {
    if (status === 'AGUARDANDO_LEITURA') {
      // Atualizar QR Code a cada 15 segundos
      const qrInterval = setInterval(() => {
        console.log('Atualizando QR Code...');
        obterQRCode();
      }, 15000);
      return () => clearInterval(qrInterval);
    }
  }, [status, obterQRCode]);

  const getStatusInfo = () => {
    switch (status) {
      case 'CARREGANDO':
        return { text: 'Carregando...', color: '#6b7280', icon: '⏳' };
      case 'CONECTADO':
        return { text: 'Conectado', color: '#10b981', icon: '✔️' };
      case 'AGUARDANDO_LEITURA':
        return { text: 'Aguardando Leitura do QR Code', color: '#f59e0b', icon: '📱' };
      case 'AGUARDANDO_QR':
        return { text: 'Gerando QR Code...', color: '#3b82f6', icon: '⏳' };
      case 'CONECTANDO':
        return { text: 'Conectando WhatsApp...', color: '#3b82f6', icon: '⏳' };
      case 'DESCONECTADO':
        return { text: 'Desconectado', color: '#6b7280', icon: '⏳' };
      case 'BLOQUEADO':
        return { text: 'Bloqueado', color: '#ef4444', icon: '🚫' };
      case 'ERRO':
        return { text: 'Erro', color: '#ef4444', icon: '⚠️' };
      default:
        return { text: status, color: '#6b7280', icon: '❓' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="app-container">
      <div className="content-card">
        <div className="header">
          <img 
            src="/assets/logo.png" 
            alt="Logo" 
            className="logo-image"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="logo-icon" style={{ display: 'none' }}>WA</div>
          <h1 className="product-name">WhatsApp Up2 Web</h1>
          {empresaNome && <p className="company-name">{empresaNome}</p>}
        </div>

        <div className="status-container" style={{ borderColor: statusInfo.color }}>
          <span className="status-icon" style={{ color: statusInfo.color }}>
            {statusInfo.icon}
          </span>
          <span className="status-text" style={{ color: statusInfo.color }}>
            {statusInfo.text}
          </span>
        </div>

        {erro && (
          <div className="message error-message">
            <strong>Erro:</strong> {erro}
          </div>
        )}
        
        {mensagem && !erro && (
          <div className="message info-message">
            {mensagem}
          </div>
        )}

        {status === 'AGUARDANDO_LEITURA' && qrCode && (
          <div className="qr-container">
            <p className="instruction-text">
              Abra o WhatsApp no seu celular, acesse <strong>Configurações → Aparelhos conectados</strong> e escaneie o QR Code abaixo:
            </p>
            <div className="qr-code-wrapper">
              <img src={qrCode} alt="QR Code WhatsApp" className="qr-code" />
            </div>
          </div>
        )}

        {status === 'CONECTADO' && (
          <div className="success-container">
            <div className="success-icon">✓</div>
            <p className="success-text">
              Seu WhatsApp está conectado e pronto para uso!
            </p>
          </div>
        )}

        {(status === 'DESCONECTADO' || status === 'ERRO') && (
          <div className="retry-container">
            <button className="retry-button" onClick={iniciarWhatsApp}>
              Tentar gerar QR Code novamente
            </button>
          </div>
        )}

        {status === 'BLOQUEADO' && (
          <div className="blocked-container">
            <div className="blocked-icon">🚫</div>
            <p className="blocked-text">
              Acesso bloqueado. Entre em contato com o suporte.
            </p>
          </div>
        )}
      </div>

      <div className="footer">
        <p>Powered by MasterVendas</p>
      </div>
    </div>
  );
}

// Componente de proteção de rota
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('adminToken');
  
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Página principal do QR Code */}
        <Route path="/" element={<QRCodePage />} />
        <Route path="/empresa/consentimentos" element={<CompanyConsentPortal />} />
        
        {/* Rotas do Admin Panel */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/empresas" element={
          <ProtectedRoute>
            <AdminEmpresas />
          </ProtectedRoute>
        } />
        <Route path="/admin/logs" element={
          <ProtectedRoute>
            <AdminLogs />
          </ProtectedRoute>
        } />
        <Route path="/admin/sessoes" element={
          <ProtectedRoute>
            <AdminSessoes />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin/reset-password" element={<AdminForgotPassword />} />
        
        {/* Fallback - redireciona para home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
